import useComponentSize from '@rehooks/component-size';
import { ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '../../cdl/IconButton/IconButton';
import { Text } from '../../cdl/Text/Text';
import { translate } from '../helpers/translate.helper';
import { IconX } from '../icons/cdl/X';
import { CloselinkDiamond } from '../icons/closelink/CloselinkDiamond';
import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

import {
    OVERLAY_SIDE_PANEL_EASING,
    OVERLAY_SIDE_PANEL_ENTER_DURATION,
    OVERLAY_SIDE_PANEL_EXIT_DURATION,
} from './overlaySidePanel.constants';
import { OverlaySidePanelBackgroundContext } from './OverlaySidePanelBackgroundContext';

const PanelBackground = styled.div<{
    $maxContentWidth: string;
    $open: boolean;
}>`
    position: absolute;
    inset: 0 0 0 auto;
    width: max(0px, calc((100% - ${({ $maxContentWidth }) => $maxContentWidth}) / 2));
    overflow: hidden;
    pointer-events: none;

    &::after {
        position: absolute;
        inset: 0;
        background: ${theme.colors.background.subtle};
        visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
        transform: ${({ $open }) => ($open ? 'translateX(0)' : 'translateX(-100%)')};
        transition:
            transform ${({ $open }) => ($open ? OVERLAY_SIDE_PANEL_ENTER_DURATION : OVERLAY_SIDE_PANEL_EXIT_DURATION)}ms
                ${OVERLAY_SIDE_PANEL_EASING},
            visibility 0s ${({ $open }) => ($open ? 0 : OVERLAY_SIDE_PANEL_EXIT_DURATION)}ms;
        content: '';

        @media (prefers-reduced-motion: reduce) {
            transition: none;
        }
    }
`;

interface LogoHeaderProps {
    title?: ReactNode;
    children: ReactNode;
    trailingVisual?: ReactNode;
    loadingOverlay?: ReactNode;
    onClose?: () => void;
    isCloseDisabled?: boolean;
    maxContentWidth?: string;
    zIndex?: number;
}

export const OverlayHeader = ({
    title,
    trailingVisual,
    loadingOverlay,
    onClose,
    isCloseDisabled,
    children,
    maxContentWidth = theme.maxContentWidths.regular,
    zIndex = theme.zIndex.overlayHeader,
}: LogoHeaderProps) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const { height } = useComponentSize(headerRef);
    const [panelOpen, setPanelOpen] = useState(false);

    return (
        <>
            <Box
                ref={headerRef}
                position="fixed"
                display="flex"
                justifyContent="space-between"
                backgroundColor="background.default"
                alignItems="center"
                top={0}
                left={0}
                right={0}
                zIndex={zIndex}
                boxShadow="medium"
                paddingX={32}
                paddingY={16}
                minHeight="64px"
            >
                <Box display="flex" alignItems="center" gap={16}>
                    <Box borderRight={1} paddingRight={onClose ? 8 : 16}>
                        {onClose ? (
                            <IconButton
                                type="button"
                                onClick={onClose}
                                disabled={isCloseDisabled}
                                Icon={IconX}
                                tooltipLabel={translate('global.close')}
                            />
                        ) : (
                            <CloselinkDiamond color="blue" height={22} />
                        )}
                    </Box>

                    {title ? typeof title === 'string' ? <Text variant="title">{title}</Text> : title : null}
                </Box>

                {trailingVisual ? <div>{trailingVisual}</div> : null}
            </Box>
            <Box position="fixed" top={`${height}px`} bottom={0} left={0} right={0} overflow="auto">
                <PanelBackground $maxContentWidth={maxContentWidth} $open={panelOpen} aria-hidden />
                <OverlaySidePanelBackgroundContext.Provider value={setPanelOpen}>
                    <Box position="relative" zIndex={1} height="100%" maxWidth={maxContentWidth} marginX="auto">
                        {children}
                    </Box>
                </OverlaySidePanelBackgroundContext.Provider>
            </Box>
            {loadingOverlay ? (
                <Box
                    position="fixed"
                    top={`${height}px`}
                    bottom={0}
                    left={0}
                    right={0}
                    zIndex={zIndex - 1}
                    style={{ pointerEvents: 'none' }}
                >
                    {loadingOverlay}
                </Box>
            ) : null}
        </>
    );
};
